// Sending a support request, including when the phone cannot reach us.
export interface SupportRequest {
  // Free text only, and optional. Whoever is on shift is not picking from a list
  // while something is going wrong.
  message: string
  diagnostics: Record<string, unknown>
  raised_at: string
}

// Kept apart from the scan store on purpose: a handful of short messages, not an
// archive, and nothing here has to survive the way a scan does.
const STORAGE_KEY = 'scan-support-queue'

// Bounded, because this is the worst possible moment for a spinner that never resolves:
// whoever pressed the button already has a problem, and send() below picks between the
// confirmation and the fallback showing a number to call purely on what post() returns.
// A request with no ceiling reaches neither, and drain() would stop on the first stalled
// one with the rest of the queue behind it.
//
// Cheap to give up on. An unreachable request goes back on the queue, and if it did in
// fact land, the server's throttle answers the repeat with a 4xx, which post() already
// reads as delivered rather than as a failure. No ofetch retry is asked for, and it does
// not retry a POST by default: drain() is the retry.
const SUPPORT_TIMEOUT_MS = 8000

const readQueue = (): SupportRequest[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  } catch {
    return []
  }
}

const writeQueue = (queue: SupportRequest[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(queue))
}

export const useSupportRequest = () => {
  const config = useRuntimeConfig()
  const device = useScanDevice()
  const online = useOnline()
  const nativeInfo = useNativeInfo()

  const pending = useState('support-pending', () => 0)

  const refreshPending = () => {
    pending.value = readQueue().length
  }

  // Everything we would otherwise have to ask a stressed staffer for over the phone.
  // Read at the moment the button is pressed, so a request delivered later still
  // describes how the phone was when the problem happened.
  const diagnostics = async (): Promise<Record<string, unknown>> => {
    // Waited for rather than read straight off. The help button can be pressed within a
    // second of the app opening, which is early enough to beat the bridge, and a request
    // that arrives without the serial has lost the one field that says which phone to
    // walk to. Resolves immediately on the web and cannot reject.
    await nativeInfoReady()

    return {
      partner: device.value.partnerName,
      device_label: device.value.deviceLabel,
      area: device.value.areaName,
      event_id: device.value.eventId,
      online: online.value,
      // Real counts from the phone's own store rather than placeholders. For the
      // pairing this phone is on, which is what a partner reporting a problem means.
      pending_count: await countByStatus(device.value.pairingId, 'pending'),
      synced_count: await countByStatus(device.value.pairingId, 'synced'),
      rejected_count: await countByStatus(device.value.pairingId, 'rejected'),
      // Waiting scans left over from an earlier pairing, which no upload will clear.
      // Worth carrying, since it explains a phone that looks stuck and is not.
      stranded_count: await countStranded(device.value.pairingId),
      // What happened last time the scanner tried to start. Safari will not answer a
      // permissions query for the camera, so this is the only reliable signal.
      camera_permission: useCameraStatus().value,
      app_version: config.public.appVersion || 'dev',
      // Which fleet this phone is from, and for an APK the version Android has installed.
      // The two fleets go wrong in different ways and the user agent does not separate
      // them, so this is the first thing worth knowing about a phone in trouble.
      platform: nativeInfo.value.platform,
      app_build: nativeInfo.value.build,
      user_agent: navigator.userAgent,
      // Which handset, as opposed to which row in the database. The label above says
      // "Device 14", and finding the phone that answers to it used to mean a database
      // query and then guessing through the Knox Manage console. The serial is what that
      // console lists and what is printed on the handset, so it is the field that ends
      // the search. Absent on the iPhones, which cannot know any of this.
      ...identityPayload(nativeInfo.value),
    }
  }

  // Three outcomes rather than two. Treating "the server said no" as a failure is
  // what would make a refused request retry for ever and then arrive several times
  // over once the limit lifts.
  type PostResult = 'sent' | 'answered' | 'unreachable'

  const post = async (payload: SupportRequest): Promise<PostResult> => {
    try {
      await $fetch(`${config.public.apiBase}/scan/v1/support`, {
        method: 'POST',
        timeout: SUPPORT_TIMEOUT_MS,
        body: payload,
        // The device key stands in for a login. Stored against this address only
        // and never as a cookie shared with the other WeAreDevelopers sites.
        headers: { Authorization: `Bearer ${device.value.token ?? ''}` },
      })

      return 'sent'
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number }, statusCode?: number })
        ?.response?.status
        ?? (error as { statusCode?: number })?.statusCode

      // The server received the request and judged it, most often as one message
      // too many. Sending it again cannot change that answer.
      if (status && status >= 400 && status < 500) {
        return 'answered'
      }

      return 'unreachable'
    }
  }

  /**
   * Tries to send straight away and keeps the request on the phone when that
   * fails. Returns whether it went through, which is what decides between the
   * plain confirmation and the fallback showing a number to call.
   */
  const send = async (message: string): Promise<boolean> => {
    const payload: SupportRequest = {
      message,
      diagnostics: await diagnostics(),
      raised_at: new Date().toISOString(),
    }

    if (online.value) {
      const result = await post(payload)

      // A refusal counts as delivered for the person holding the phone: we already
      // have a message about this device and someone is coming.
      if (result !== 'unreachable') {
        return true
      }
    }

    writeQueue([...readQueue(), payload])
    refreshPending()

    return false
  }

  /**
   * Delivers anything raised while offline. Only a request that could not reach us
   * stays on the phone. One the server answered is finished with, either way.
   */
  const drain = async () => {
    const queue = readQueue()

    if (!queue.length || !online.value) {
      return
    }

    const remaining: SupportRequest[] = []

    for (const request of queue) {
      if (await post(request) === 'unreachable') {
        remaining.push(request)
      }
    }

    writeQueue(remaining)
    refreshPending()
  }

  return { send, drain, pending, refreshPending }
}
